/**
 * 1 — Background removal (AI): the removal action, its toggles, and the live status.
 *
 * Extracted from `MaskEditor.tsx` (WS-J J-12). Preserved verbatim: the AI-settings gear popover
 * (edge calibration + AI edge + the per-stage toggles + artwork shading), the saved-removal retry
 * banner, the low-RAM `Fast` default explanation, and the segmenter / compositor diagnostic lines.
 * 
 * `isLowMemoryDevice` and `defaultMatteQuality` moved here with the explanation they justify — the
 * card is the only place either is read, and `MaskEditor.tsx` imports `defaultMatteQuality` back for
 * the `matteQuality` seed.
 */
import { InfoHint } from "../../ui/Tooltip.js";
import { PRINT_LIGHT_HELP, valueHelpText } from "../../ui/valueHelp.js";
import { useSessionStore } from "../../stores/session.js";
import type { CompositorInfo, EngineBinding, SegmenterInfo } from "@genz/engine-bind";
import { Icon } from "../../ui/Icon.js";
import { InlineRetry } from "../../ui/InlineRetry.js";
import { KEY_DEPTH_MAX, KEY_DEPTH_MIN, resolveCalibration } from "../../stores/segmenterCalibration.js";
import type { EditorRefs } from "../editorContext.js";
import type { MatteKind } from "../maskEditorTypes.js";
import type { AiStatus } from "../useMockupBake.js";

/** Cheap low-RAM signal: `navigator.deviceMemory` (approx device RAM in GB — Chromium/WebView2
 *  only, which IS our desktop runtime; undefined elsewhere). The spec CAPS this at 8 for privacy
 *  (it only ever returns 0.25/0.5/1/2/4/8), so `8` means "8 GB OR MORE" and can't be told apart
 *  from a 16/32 GB workstation — a `<= 8` test would match every machine and silently disable the
 *  quality default for everyone. We treat `<= 4` as genuinely low-RAM: the "quality" matte
 *  (BiRefNet) peaks ~7.3 GB during inference, so a ≤4 GB box pages and blows the 90s editor
 *  timeout → "Remove background" reads as broken; we prefer the tiny U²-Net-p ("fast") matte
 *  there. (An 8 GB box can still stall, but it's indistinguishable from 16 GB+ here, so we leave
 *  it on quality and let the existing timeout message guide it to Fast.) Returns false when the
 *  signal is absent — never downgrade a machine we can't size. */
function isLowMemoryDevice(): boolean {
  if (typeof navigator === "undefined") return false;
  const mem = (navigator as unknown as { deviceMemory?: number }).deviceMemory;
  return typeof mem === "number" && mem <= 4;
}

/** Default matte quality by machine capability: a low-core / low-RAM machine (common on budget
 *  Windows) defaults to FAST (U²-Net-p, ~1s, ~4.5 MB) so the heavy BiRefNet CPU pass can't peg /
 *  overheat it OR page against ~7 GB and blow the editor timeout; capable machines default to
 *  QUALITY. Belt-and-suspenders alongside the raw-body IPC fix — the operator can still flip it
 *  (an explicit Quality pick is never overridden). */
export function defaultMatteQuality(): "fast" | "quality" {
  if (typeof navigator === "undefined") return "quality";
  const cores = navigator.hardwareConcurrency ?? 8;
  return cores <= 2 || isLowMemoryDevice() ? "fast" : "quality";
}

/**
 * The SHIPPED print-area finish — the one source of the four print-light defaults.
 *
 * Calibrated against the owner's reference sample on 2026-07-31: gradient 0.42 reproduces the
 * target's 25% top-to-bottom glare fade, grain 0.55 hits its measured matte texture (flat-purple
 * sd ≈ 6, flat-yellow ≈ 3.4 — the engine grain is shadow-weighted) and sheen 1.2 reproduces its
 * glare (its lit-zone p90 sits ~21 luma over its median).
 *
 * `highlights` is the odd one out: it is NOT measured off the reference sample, because that
 * sample was shot with a light design. It drives the ADDITIVE pass (engine
 * `ShadingParams.highlights`) that makes a BLACK design catch the case's light at all, and 40%
 * is the shipped NEW-TEMPLATE default — enough that a pure-black print reads as shaded next to
 * a white one, well short of the 100% that matches white's full shadow range and starts to look
 * lacquered. EXISTING templates are unaffected: `useLayerAssets` restores a saved calibration
 * that has no `highlights` key to 0, so only a template created from here on opens at 40%.
 *
 * `MaskEditor`'s four `useState` seeds AND the `reference` preset below both read THIS, so they
 * cannot drift (review-1 F2 / mutant M10: they were two independent copies of the same three
 * numbers, and retuning one left every test green while a new template opened with all four
 * presets dark). Retune the finish here and both halves move together.
 *
 * Annotated rather than `as const`: the literal types an `as const` produces would narrow
 * `MaskEditor`'s `useState` seeds to `0.42` / `1.2` / `0.55` / `0.40`, and its setters would
 * then no longer satisfy this card's `(v: number) => void` props.
 */
export const PRINT_LIGHT_DEFAULTS: Readonly<{ gradient: number; sheen: number; grain: number; highlights: number }> =
  { gradient: 0.42, sheen: 1.2, grain: 0.55, highlights: 0.40 };

/**
 * The "Flat" finish: no synthetic light at all. Every new template, a Higgsfield one included,
 * now starts on `PRINT_LIGHT_DEFAULTS` ("Reference"): on a flat magenta base, Flat did not look
 * printed on anything (owner, 2026-10-02). The preset below and `MaskEditor`'s optional
 * `initialPrintLight="flat"` seed both read THIS, for the same reason `PRINT_LIGHT_DEFAULTS` is shared.
 */
export const PRINT_LIGHT_FLAT: Readonly<{ gradient: number; sheen: number; grain: number; highlights: number }> =
  { gradient: 0, sheen: 0, grain: 0, highlights: 0 };

/**
 * Print-area FINISH presets (WS-X) — one click for the four print-light values.
 *
 * `reference` IS `PRINT_LIGHT_DEFAULTS`, by construction. The other three bracket it so "more
 * gradient" / "less gradient" is a button rather than three drags.
 *
 * A preset owns ONLY these three. The artwork-shading STRENGTH and the shading stage toggle are
 * a different decision (how much of the case's own recorded light to transfer, and whether to
 * transfer it at all), so a preset never moves them.
 */
const PRINT_LIGHT_PRESETS = [
  { id: "flat", label: "Flat", ...PRINT_LIGHT_FLAT },
  { id: "soft", label: "Soft", gradient: 0.20, sheen: 0.50, grain: 0.30, highlights: 0.25 },
  { id: "reference", label: "Reference", ...PRINT_LIGHT_DEFAULTS },
  { id: "strong", label: "Strong", gradient: 0.80, sheen: 1.6, grain: 0.55, highlights: 0.60 },
] as const;

/** Slider values are authored in whole percent, so an exact compare would still be hostage to
 *  float round-trips through a saved calibration: the engine's `ShadingParams.gradient` is an
 *  **f32** (`engine/genz-engine/src/model/render_settings.rs:449`), so a calibration that has been
 *  through the engine stores `Math.fround(0.42)` = 0.41999998688697815 — 1.3e-8 off, identical on
 *  a 1%-step slider, and `===` would silently unlight Reference on every template in the field.
 *  Half a percent is well inside one slider step, so it cannot swallow a deliberate 1% nudge
 *  either. Both sides are pinned in `controls.test.tsx` ("Reference stays lit when the stored
 *  calibration has been through an f32 round-trip…" / "a value a full slider step away…"); do not
 *  replace this with `===`. */
function matchesPreset(p: (typeof PRINT_LIGHT_PRESETS)[number], gradient: number, sheen: number, grain: number, highlights: number): boolean {
  const near = (a: number, b: number) => Math.abs(a - b) < 0.005;
  return near(p.gradient, gradient) && near(p.sheen, sheen) && near(p.grain, grain) && near(p.highlights, highlights);
}

export interface AiRemovalCardProps {
  locked?: boolean;
  engine: EngineBinding;
  editorRefs: EditorRefs;
  aiAvailable: boolean;
  aiBusy: boolean;
  aiElapsed: number;
  aiStatus: AiStatus | null;
  segInfo: SegmenterInfo | null;
  compInfo: CompositorInfo | null;
  chromaReady: boolean;
  removed: boolean;
  matteRestoreFailed: boolean;
  /** The saved-matte restore is running. The editor reads "not removed" until it lands, so
   *  this is what stops that from being a silent lie for the length of the fetch. */
  matteRestoring: boolean;
  retryMatteRestore: () => void;
  autoRemoveBg: () => void | Promise<void>;
  cancelAutoRemoveBg: () => void;
  removeByMagentaKey: () => void;
  resetBackground: () => void;
  matteKindRef: React.RefObject<MatteKind | null>;
  matteQuality: "fast" | "quality";
  setMatteQuality: React.Dispatch<React.SetStateAction<"fast" | "quality">>;
  protectProduct: boolean;
  setProtectProduct: React.Dispatch<React.SetStateAction<boolean>>;
  showAiSettings: boolean;
  setShowAiSettings: React.Dispatch<React.SetStateAction<boolean>>;
  aiEdge: boolean;
  setAiEdge: React.Dispatch<React.SetStateAction<boolean>>;
  aiEdgeKeep: number;
  setAiEdgeKeep: (v: number) => void;
  edgeChoke: number;
  setEdgeChoke: (v: number) => void;
  edgeFeather: number;
  setEdgeFeather: (v: number) => void;
  matteStrength: number;
  setMatteStrength: (v: number) => void;
  stageDespill: boolean;
  setStageDespill: React.Dispatch<React.SetStateAction<boolean>>;
  stageRefine: boolean;
  setStageRefine: React.Dispatch<React.SetStateAction<boolean>>;
  stageMatteStrength: boolean;
  setStageMatteStrength: React.Dispatch<React.SetStateAction<boolean>>;
  stageShading: boolean;
  setStageShading: React.Dispatch<React.SetStateAction<boolean>>;
  printShadingStrength: number;
  setPrintShadingStrength: (v: number) => void;
  shadeGradient: number;
  setShadeGradient: (v: number) => void;
  shadeGrain: number;
  setShadeGrain: (v: number) => void;
  shadeSheen: number;
  setShadeSheen: (v: number) => void;
  shadeHighlights: number;
  setShadeHighlights: (v: number) => void;
  shadeShadowDepth: number;
  setShadeShadowDepth: (v: number) => void;
  shadeHighlightDepth: number;
  setShadeHighlightDepth: (v: number) => void;
  shadeInkBlack: number;
  setShadeInkBlack: (v: number) => void;
  shadeInkWhite: number;
  setShadeInkWhite: (v: number) => void;
  /** The platform's depth applies to every template: the two sliders show it and cannot move. */
}

export function AiRemovalCard(p: AiRemovalCardProps) {
  const {
    locked, engine, editorRefs, aiAvailable, aiBusy,
    aiElapsed, aiStatus, segInfo, compInfo, chromaReady,
    removed, matteRestoreFailed, matteRestoring, retryMatteRestore, autoRemoveBg,
    cancelAutoRemoveBg, removeByMagentaKey, resetBackground, matteKindRef, matteQuality,
    setMatteQuality, protectProduct, setProtectProduct, showAiSettings, setShowAiSettings,
    aiEdge, setAiEdge, aiEdgeKeep, setAiEdgeKeep, edgeChoke,
    setEdgeChoke, edgeFeather, setEdgeFeather, matteStrength, setMatteStrength,
    stageDespill, setStageDespill, stageRefine, setStageRefine, stageMatteStrength,
    setStageMatteStrength, stageShading, setStageShading, printShadingStrength, setPrintShadingStrength,
    shadeGradient, setShadeGradient, shadeGrain, setShadeGrain, shadeSheen,
    setShadeSheen, shadeHighlights, setShadeHighlights,
    shadeShadowDepth, setShadeShadowDepth, shadeHighlightDepth, setShadeHighlightDepth,
    shadeInkBlack, setShadeInkBlack, shadeInkWhite, setShadeInkWhite,
  } = p;
  const admin = useSessionStore((st) => st.isAdmin());
  return (
    <>
        {/* 1 — Background removal (AI): the removal action + its protect/quality toggles, the
            saved-removal retry banner, and the live AI-matte / segmenter status. */}
        {/* WS-Z6 / R12: the private `<details>` this card drew is gone — the rail is one
            `ui/Accordion.tsx`, and MaskEditor owns the head, the marker and the open key. The
            card is the accordion BODY, so the section keeps its `data-testid` and every
            control inside it. */}
        <div className="mask-editor__section-body" data-testid="section-bg-removal">
          <div className="mask-editor__chroma-row">
            <button
              type="button"
              /* WS-Z6 / rule 7: the page's ONE primary lives in its action bar — the template
                 editor's Publish, the wizard's Next. Removing the background is the rail's
                 headline action, and the rail is a panel inside a page, not the page. */
              className="btn btn--sm"
              onClick={() => {
                if (removed) resetBackground();
                else void autoRemoveBg();
              }}
              /* This button is TWO actions. While `removed` is false it runs the AI matte, so it
                 needs `aiAvailable`. Once `removed` is true it is `resetBackground` — a re-draw of
                 the original case photo that loads no model. Gating THAT on AI trapped every
                 no-AI build that used "Print guide key (no AI)": the key sets `removed`, which hides
                 the magenta button (`!removed` below), and the restore was disabled — leaving no
                 control at all. AI availability now gates only the AI half. */
              disabled={locked || !chromaReady || aiBusy || (!removed && !aiAvailable)}
              title={
                removed
                  ? "Restore the original background (undo the removal)"
                  : aiAvailable
                    ? "Remove the background with the AI matte (BiRefNet) and detect the print area from the print guide"
                    : "AI background removal runs only in the desktop app"
              }
              data-testid="chroma-remove"
            >
              {aiBusy ? `Working… ${aiElapsed}s` : removed ? <><Icon name="undo" size={16} /> Restore background</> : "Remove background (AI)"}
            </button>
            {!removed && (
              <button
                type="button"
                className="btn btn--sm"
                onClick={removeByMagentaKey}
                disabled={locked || !chromaReady || aiBusy}
                title="Key the print guide panel with the deterministic chroma key — the Transparent tab's exact technique. No AI model, instant, identical output on every machine. Needs the magenta marker; use the AI for ordinary photos."
                data-testid="chroma-remove-magenta"
              >
                Print guide key (no AI)
              </button>
            )}
            {aiBusy && (
              /* Escape hatch (Windows hang report): asks the engine to abort the native inference
                 when the binding supports it, then releases the UI and stales the run via the
                 request id — a late native result is DISCARDED (autoRemoveReqRef check at the
                 commit site), so cancelling can never paint a stale matte. On a binding with no
                 cancel the native pass still finishes in the background; the app stays usable
                 either way. See `cancelAutoRemoveBg`. */
              <button
                type="button"
                className="btn btn--sm btn--ghost"
                onClick={cancelAutoRemoveBg}
                data-testid="chroma-remove-cancel"
              >
                Cancel
              </button>
            )}
            {/* AI settings gear (mirrors the UV settings popup): consolidates matte quality, protect
                product, AI edge + its calibration sliders, artwork shading, and the per-stage
                show/hide toggles — collapsed by default so the row above stays uncluttered. */}
            <button
              type="button"
              className="btn btn--sm btn--ghost"
              aria-expanded={showAiSettings}
              aria-label="AI settings"
              title="AI settings"
              data-testid="ai-settings-toggle"
              onClick={() => setShowAiSettings((v) => !v)}
            >
              <Icon name="settings" size={16} /> AI settings
            </button>
          </div>
          {/* Print-area light (WS-X) — the finish of the PRINT AREA itself: how hard the
              synthetic studio light ramps across it, how glossy it reads, and how much matte
              grain sits on top. These four lived inside the gear popup, which is why the owner
              reported "very limited controls" while looking straight at the one thing they
              wanted to dial. They are an always-visible group now; the gear keeps the MATTE
              controls (quality, protect product, AI edge, edge calibration, stage toggles) and
              its Reset still owns the shading values it always reset. */}
          <div className="mask-editor__print-light" data-testid="print-area-light">
            {/* The house caption voice (`theme.css` `.caption`: 12 / 400 / `--text-faint`), not a
                bespoke one. `docs/design.md` allows exactly one label voice and one caption voice,
                and names "13 px semibold in the next panel" as the regression that rule exists to
                stop; a 12/600 group heading would have been a sixth. `.caption` already sets the
                faint colour, so the `faint` class it used to carry is redundant. */}
            <span className="caption">Print-area light</span>
            {/* Named group: without it a screen reader announces four unrelated "Flat, toggle
                button" / "Soft, toggle button" items with nothing saying what they preset. The
                per-button `title` carries the three percentages, but a title is not an accessible
                name. (Four mutually-exclusive options as `aria-pressed` toggles rather than a
                radiogroup is the house pattern — see `.templates__item[aria-pressed]`.) */}
            <div className="mask-editor__chroma-row" role="group" aria-label="Print-area light presets" data-testid="print-light-presets">
              {PRINT_LIGHT_PRESETS.map((preset) => (
                <button
                  key={preset.id}
                  type="button"
                  className={`btn btn--sm${matchesPreset(preset, shadeGradient, shadeSheen, shadeGrain, shadeHighlights) ? " is-active" : ""}`}
                  onClick={() => { setShadeGradient(preset.gradient); setShadeSheen(preset.sheen); setShadeGrain(preset.grain); setShadeHighlights(preset.highlights); }}
                  disabled={locked}
                  aria-pressed={matchesPreset(preset, shadeGradient, shadeSheen, shadeGrain, shadeHighlights)}
                  title={`Light gradient ${Math.round(preset.gradient * 100)}% · Gloss sheen ${Math.round(preset.sheen * 100)}% · Surface grain ${Math.round(preset.grain * 100)}% · Highlights ${Math.round(preset.highlights * 100)}%`}
                  data-testid={`print-light-preset-${preset.id}`}
                >
                  {preset.label}
                </button>
              ))}
            </div>
            {/* Artwork shading (B) — multiplies the case's own print-region shading over the placed
                design; works regardless of removal (compose-time only), so it's never gated on the
                matte like the controls below. */}
            <label className="mask-editor__chroma-tol">
              <span data-testid="help-shading"><InfoHint label="About Artwork shading" text={valueHelpText(PRINT_LIGHT_HELP.shading)} /></span>{" "}
              Artwork shading {Math.round(printShadingStrength * 100)}%
              <input
                type="range" min={0} max={150} value={Math.round(printShadingStrength * 100)}
                onChange={(e) => setPrintShadingStrength(Number(e.target.value) / 100)}
                disabled={locked} aria-label="Artwork shading" data-testid="artwork-shading-slider"
              />
            </label>
            {/* Highlights (WS-HL) — the other half of "Artwork shading", which is a MULTIPLY and
                therefore cannot lift a dark design at all (`0 x f == 0`). This ADDS the case's
                own brighter-than-median light back, weighted so black takes the full lift and
                white takes none, which is what makes a black and a white design show comparable
                depth. Sits next to the strength slider because the two are one physical effect:
                the shadow half and the light half of the same recorded panel. */}
            <label className="mask-editor__chroma-tol">
              <span data-testid="help-highlights"><InfoHint label="About Highlights" text={valueHelpText(PRINT_LIGHT_HELP.highlights)} /></span>{" "}
              Highlights {Math.round(shadeHighlights * 100)}%
              <input
                type="range" min={0} max={100} value={Math.round(shadeHighlights * 100)}
                onChange={(e) => setShadeHighlights(Number(e.target.value) / 100)}
                disabled={locked} aria-label="Highlights" data-testid="shade-highlights-slider"
              />
            </label>
            {/* The values in Admin → AI matte are the defaults; a template may hold its own
                (owner, 2026-10-13). This puts the defaults back. */}
            <button type="button" className="btn btn--ghost btn--sm" disabled={locked} data-testid="print-light-reset"
              onClick={() => {
                const d = resolveCalibration();
                setPrintShadingStrength(d.print_shading); setShadeHighlights(d.print_highlights);
                setShadeGradient(d.print_gradient); setShadeGrain(d.print_grain); setShadeSheen(d.print_sheen);
                setShadeShadowDepth(d.key_shadow_depth); setShadeHighlightDepth(d.key_highlight_depth);
                setShadeInkBlack(d.print_black); setShadeInkWhite(d.print_white);
              }}>
              Reset to defaults
            </button>
            {/* Depth of the case's OWN recorded light (owner, 2026-10-12: the keyed print area
                lost the shadow and highlight definition the magenta image had around the
                camera bump). 100% is the light exactly as recorded; the engine still clamps
                the result, so a high value pins at the clamp and cannot turn artwork black. */}
            {/* An admin's control only: a customer never calibrates, and gets the value an
                admin set for everyone in Admin → AI matte. */}
            {admin && (
              <>
              <label className="mask-editor__chroma-tol">
                <span data-testid="help-shadowDepth"><InfoHint label="About Shadow depth" text={valueHelpText(PRINT_LIGHT_HELP.shadowDepth)} /></span>{" "}
                Shadow depth {Math.round(shadeShadowDepth * 100)}%
                <input
                  type="range" min={KEY_DEPTH_MIN * 100} max={KEY_DEPTH_MAX * 100} step={5} value={Math.round(shadeShadowDepth * 100)}
                  onChange={(e) => setShadeShadowDepth(Number(e.target.value) / 100)}
                  disabled={locked} aria-label="Shadow depth" data-testid="shade-shadow-depth-slider"
                />
              </label>
              <label className="mask-editor__chroma-tol">
                <span data-testid="help-highlightDepth"><InfoHint label="About Highlight depth" text={valueHelpText(PRINT_LIGHT_HELP.highlightDepth)} /></span>{" "}
                Highlight depth {Math.round(shadeHighlightDepth * 100)}%
                <input
                  type="range" min={KEY_DEPTH_MIN * 100} max={KEY_DEPTH_MAX * 100} step={5} value={Math.round(shadeHighlightDepth * 100)}
                  onChange={(e) => setShadeHighlightDepth(Number(e.target.value) / 100)}
                  disabled={locked} aria-label="Highlight depth" data-testid="shade-highlight-depth-slider"
                />
              </label>

              </>
            )}
            {/* The design's ink range (owner, 2026-10-14): a real print has no pure black and no
                pure white. */}
            <label className="mask-editor__chroma-tol">
              <span data-testid="help-inkBlack"><InfoHint label="About Darkest black" text={valueHelpText(PRINT_LIGHT_HELP.inkBlack)} /></span>{" "}
              Darkest black {Math.round(shadeInkBlack * 100)}%
              <input type="range" min={0} max={50} value={Math.round(shadeInkBlack * 100)}
                onChange={(e) => setShadeInkBlack(Number(e.target.value) / 100)}
                disabled={locked} aria-label="Darkest black" data-testid="shade-ink-black-slider" />
            </label>
            <label className="mask-editor__chroma-tol">
              <span data-testid="help-inkWhite"><InfoHint label="About Brightest white" text={valueHelpText(PRINT_LIGHT_HELP.inkWhite)} /></span>{" "}
              Brightest white {Math.round(shadeInkWhite * 100)}%
              <input type="range" min={50} max={100} value={Math.round(shadeInkWhite * 100)}
                onChange={(e) => setShadeInkWhite(Number(e.target.value) / 100)}
                disabled={locked} aria-label="Brightest white" data-testid="shade-ink-white-slider" />
            </label>
            {/* Synthetic studio light for flat marker panels: an AI-generated flat-magenta base
                records NO body light to transfer, so these add a top-light ramp + print grain
                on top of whatever real shading the base does carry. Both 0 = engine-off. */}
            {/* Ceiling 150% (WS-X), was 80%: the owner needed more ramp than 80% could give on an
                optically flat panel. Nothing can blow out — the engine multiplies the factor by
                `1 + gradient·(0.5 − ny)` (`render/mockup.rs:354-357`) and then clamps it into the
                `ShadingParams` min/max window (0.30 / 1.70, normalized at `mockup.rs:321-331`) at
                `mockup.rs:368`, so gradient 1.5 pins at the clamp rather than running past it. On
                a flat panel only the top and bottom ~3.3% of the print region actually saturate. */}
            <label className="mask-editor__chroma-tol">
              <span data-testid="help-gradient"><InfoHint label="About Light gradient" text={valueHelpText(PRINT_LIGHT_HELP.gradient)} /></span>{" "}
              Light gradient {Math.round(shadeGradient * 100)}%
              <input
                type="range" min={0} max={150} step={1} value={Math.round(shadeGradient * 100)}
                onChange={(e) => setShadeGradient(Number(e.target.value) / 100)}
                disabled={locked} aria-label="Light gradient" data-testid="shade-gradient-slider"
              />
            </label>
            <label className="mask-editor__chroma-tol">
              <span data-testid="help-grain"><InfoHint label="About Surface grain" text={valueHelpText(PRINT_LIGHT_HELP.grain)} /></span>{" "}
              Surface grain {Math.round(shadeGrain * 100)}%
              <input
                type="range" min={0} max={100} value={Math.round(shadeGrain * 100)}
                onChange={(e) => setShadeGrain(Number(e.target.value) / 100)}
                disabled={locked} aria-label="Surface grain" data-testid="shade-grain-slider"
              />
            </label>
            <label className="mask-editor__chroma-tol">
              <span data-testid="help-sheen"><InfoHint label="About Gloss sheen" text={valueHelpText(PRINT_LIGHT_HELP.sheen)} /></span>{" "}
              Gloss sheen {Math.round(shadeSheen * 100)}%
              <input
                type="range" min={0} max={200} value={Math.round(shadeSheen * 100)}
                onChange={(e) => setShadeSheen(Number(e.target.value) / 100)}
                disabled={locked} aria-label="Gloss sheen" data-testid="shade-sheen-slider"
              />
            </label>
            <label className="mask-editor__opacity-row mask-editor__opacity-row--inline">
              <input
                type="checkbox"
                checked={stageShading}
                onChange={(e) => setStageShading(e.target.checked)}
                disabled={locked}
                data-testid="stage-shading"
              />
              Artwork shading on
            </label>
            {/* Where the change lands. The sliders re-run the LIVE preview at once, but a
                GENERATED mockup composes from the SAVED calibration (hydrated back through
                `sanitizeShading`, templates/bakedSync.ts), so a tune that was never saved reads
                as "only limited controls are taking effect" — which is half of what the owner
                reported. `sanitizeShading` is a VALIDITY guard on that stored row, not the reason
                the slider ranges are safe: what bounds them is the engine's own clamp (see the
                Light gradient comment above). */}
            <div className="faint mask-editor__hint--quiet" data-testid="print-area-light-hint">
              Changes show in the preview now and reach generated mockups after Save.
            </div>
          </div>

          {showAiSettings && (
            <div className="mask-editor__ai-settings mask-editor__subpanel" data-testid="ai-settings-panel">
              <div className="row row--between">
                <span className="faint mask-editor__subpanel-title">AI settings</span>
                <button
                  type="button"
                  className="btn btn--sm btn--ghost"
                  disabled={locked}
                  onClick={() => {
                    setPrintShadingStrength(0.6);
                    setStageDespill(true);
                    setStageRefine(true);
                    setStageMatteStrength(true);
                    setStageShading(true);
                    setEdgeChoke(resolveCalibration().edge_choke_px);
                    setEdgeFeather(resolveCalibration().edge_feather_px);
                    setMatteStrength(0);
                    setAiEdgeKeep(0);
                    setAiEdge(false);
                  }}
                  data-testid="ai-settings-reset"
                  /* WS-X moved two of the five controls this resets — Artwork shading and its
                     on/off — OUT of this popup and into the always-visible Print-area light group,
                     so the title now says what the operator will actually see move, and says that
                     the four light presets are NOT part of it. Pinned by "Reset restores the
                     artwork shading + its stage toggle but leaves the three finish values (and the
                     lit preset) alone" in MaskEditor.test.tsx. */
                  title="Reset the AI settings to their defaults. This also restores Artwork shading and its on/off in the Print-area light group above; the four light presets are not affected."
                >
                  Reset
                </button>
              </div>

              {/* Matte quality + Protect product (relocated from the row above). */}
              <div className="mask-editor__chroma-row">
                <button
                  type="button"
                  className={`btn btn--sm${matteQuality === "fast" ? " is-active" : ""}`}
                  onClick={() => setMatteQuality((q) => (q === "fast" ? "quality" : "fast"))}
                  disabled={locked || aiBusy}
                  aria-pressed={matteQuality === "fast"}
                  title="Fast = U²-Net-p (~1s, lighter). Quality = BiRefNet (cleanest edge, ~12s on CPU). Pick Fast when the removal is too slow."
                  data-testid="matte-quality"
                >
                  {matteQuality === "fast" ? <><Icon name="bolt" size={16} /> Fast matte</> : <><Icon name="sparkle" size={16} /> Quality matte</>}
                </button>
                <button
                  type="button"
                  className={`btn btn--sm${protectProduct ? " is-active" : ""}`}
                  onClick={() => setProtectProduct((v) => !v)}
                  disabled={locked}
                  aria-pressed={protectProduct}
                  title="Keep the print-area region during removal (stops it eating the product/case)"
                  data-testid="chroma-protect-product"
                >
                  <Icon name="shield" size={16} /> Protect product{protectProduct ? " · on" : ""}
                </button>
              </div>

              {/* AI edge + its safety skip badge + protect-content slider (relocated). */}
              <div className="mask-editor__chroma-row">
                <button
                  type="button"
                  className={`btn btn--sm${aiEdge ? " is-active" : ""}`}
                  onClick={() => setAiEdge((v) => !v)}
                  disabled={locked}
                  aria-pressed={aiEdge}
                  title="AI edge: cut the camera / raised details out of the print area (using the AI matte) so the design doesn't cover them. Run 'Remove background (AI)' first. Safe — if the matte can't isolate small content, it keeps the marker print area instead of blanking it."
                  data-testid="ai-edge"
                >
                  <Icon name="brain" size={16} /> AI edge{aiEdge ? " · on" : ""}
                </button>
                {aiEdge && aiStatus?.aiEdge === "skipped" && (
                  <span
                    className="badge badge--warn mask-editor__skip-badge"
                    data-testid="ai-edge-skipped-badge"
                    title="Safety guard: refining would have removed more than half the print area (the matte kept most of the panel), so AI edge was skipped and the reliable marker mask kept."
                  >
                    Skipped — cutout covers most of the print area; lower "protect content"
                  </span>
                )}
              </div>
              {aiEdge && !editorRefs.matteAlphaRef.current && (
                <div className="faint mask-editor__warn-note" data-testid="ai-edge-hint">
                  AI edge needs the matte — click <strong>Remove background (AI)</strong> first.
                </div>
              )}
              {aiEdge && editorRefs.matteAlphaRef.current && (
                <label className="mask-editor__chroma-tol mask-editor__inline-gap" data-testid="ai-edge-keep"
                  title="Raise to protect more soft/partial content near the edges; lower to let the design fill more of the panel. Blends with a smooth (feathered) transition at the edge instead of a hard cut.">
                  AI edge — protect content {aiEdgeKeep}%
                  <input
                    type="range" min={0} max={100} value={aiEdgeKeep}
                    onChange={(e) => setAiEdgeKeep(Number(e.target.value))}
                    disabled={locked} aria-label="AI edge protect content" data-testid="ai-edge-keep-slider"
                  />
                </label>
              )}

              {/* Per-template edge calibration (B3) — feather / choke / matte-strength of the AI-matte
                  cutout, seeded from the platform calibration but tuned HERE per template. Shown whenever
                  a removal produced a matte (NOT gated on AI edge — they reshape the matte itself). Live:
                  dragging any one re-keys from pristine + recomposites within ~½s (see the debounced
                  effects). A CHROMA cutout has no edge processing to calibrate — the key resolves
                  the edge analytically — so the block is replaced by a one-line note. */}
              {removed && matteKindRef.current === "chroma" && (
                <div className="mask-editor__hint mask-editor__hint--quiet" data-testid="chroma-exact-hint">
                  Magenta key: exact chroma key, no edge processing.
                </div>
              )}
              {removed && editorRefs.matteAlphaRef.current && matteKindRef.current !== "chroma" && (
                <div className="mask-editor__calibration" data-testid="edge-calibration">
                  <label className="mask-editor__chroma-tol" title="Softens the cutout edge — a larger blur gives a gentler transition around the product.">
                    Edge feather {edgeFeather}px
                    <input
                      type="range" min={0} max={10} step={1} value={edgeFeather}
                      onChange={(e) => setEdgeFeather(Number(e.target.value))}
                      disabled={locked} aria-label="Edge feather" data-testid="edge-feather-slider"
                    />
                  </label>
                  <label className="mask-editor__chroma-tol" title="Shrinks (+) or grows (−) the cutout edge — raise to eat a residual halo, lower to recover an over-trimmed edge.">
                    Edge choke {edgeChoke}px
                    <input
                      type="range" min={-5} max={10} step={1} value={edgeChoke}
                      onChange={(e) => setEdgeChoke(Number(e.target.value))}
                      disabled={locked} aria-label="Edge choke" data-testid="edge-choke-slider"
                    />
                  </label>
                  <label className="mask-editor__chroma-tol" title="Removes low-confidence halo/fill from the cutout — raise to strip faint semi-transparent leftovers around the product.">
                    Matte strength {matteStrength}
                    <input
                      type="range" min={0} max={100} step={1} value={matteStrength}
                      onChange={(e) => setMatteStrength(Number(e.target.value))}
                      disabled={locked} aria-label="Matte strength" data-testid="matte-strength-slider"
                    />
                  </label>
                </div>
              )}

              {/* Per-stage show/hide (C) — turn a removal stage off to see its effect live. Matte-only
                  stages need a matte to mean anything, so they're gated the same as the sliders above. */}
              {removed && editorRefs.matteAlphaRef.current && (
                <div className="mask-editor__ai-stages mask-editor__subpanel mask-editor__subpanel--tight" data-testid="ai-stage-toggles">
                  <span className="faint mask-editor__subpanel-title">Show stages</span>
                  <label className="mask-editor__opacity-row mask-editor__opacity-row--inline">
                    <input type="checkbox" checked={stageDespill} onChange={(e) => setStageDespill(e.target.checked)} disabled={locked} data-testid="stage-despill" />
                    Despill
                  </label>
                  <label className="mask-editor__opacity-row mask-editor__opacity-row--inline">
                    <input type="checkbox" checked={stageRefine} onChange={(e) => setStageRefine(e.target.checked)} disabled={locked} data-testid="stage-refine" />
                    Edge refine
                  </label>
                  <label className="mask-editor__opacity-row mask-editor__opacity-row--inline">
                    <input type="checkbox" checked={stageMatteStrength} onChange={(e) => setStageMatteStrength(e.target.checked)} disabled={locked} data-testid="stage-matte-strength" />
                    Matte strength
                  </label>
                </div>
              )}
            </div>
          )}
          {/* The saved AI removal couldn't be re-fetched/decoded on open (network/CORS/transient). The
              "background removed" state + calibration are NOT restored — surface a one-click retry
              instead of silently giving up (which would let the next Save wipe the saved matte). */}
          {/* The saved removal is being fetched back. Until it lands the canvas shows the
              template as "not removed", which is wrong — say so while it is happening, not
              only when it fails. `role="status"`: nothing is broken yet. */}
          {matteRestoring && !matteRestoreFailed && (
            <div className="faint mask-editor__inline-gap" role="status" data-testid="matte-restoring">
              Restoring the saved background removal…
            </div>
          )}
          {matteRestoreFailed && (
            <div className="mask-editor__inline-gap" data-testid="matte-restore-failed">
              <InlineRetry
                message="Couldn't restore the saved AI background removal."
                label="Retry"
                testId="matte-restore-retry"
                /* F-6: `runMatteRestore` refuses while the case image has not decoded, so a
                   press here would do nothing at all. Say that, rather than accepting a click
                   the editor is going to discard. */
                disabled={!chromaReady}
                disabledReason="Waiting for the case image to load — Retry will work once it is on screen."
                onRetry={() => {
                  // User-initiated retry: clear the failure latch + banner, then re-attempt.
                  retryMatteRestore();
                }}
              />
            </div>
          )}
          {aiStatus && (
            <div
              className="faint mask-editor__ai-status-line"
              data-testid="ai-status"
            >
              <span>Print area: <strong>{aiStatus.source}</strong>{aiStatus.aiEdge === "on" ? " + AI edge" : aiStatus.aiEdge === "skipped" ? <span className="mask-editor__warn-text"> (AI edge skipped)</span> : ""}</span>
              <span>Design area: <strong style={{ color: aiStatus.printPct < 2 ? "var(--danger, #d33)" : "inherit" }}>{aiStatus.printPct.toFixed(0)}%</strong></span>
              {aiStatus.mattePct > 0 && <span>Matte: {aiStatus.mattePct.toFixed(0)}%</span>}
              <span>Design: {aiStatus.hasDesign ? "loaded" : "none"}</span>
            </div>
          )}
          {aiBusy && (
            <div data-testid="ai-busy-indicator" className="mask-editor__ai-busy">
              <span className="mask-editor__ai-pulse" aria-hidden="true" />
              <span>On-device AI matte… {aiElapsed}s — runs locally on this machine (no upload).</span>
            </div>
          )}
          {engine.segmenterInfo && segInfo && (
            <div data-testid="seg-info" className="faint mask-editor__seg-info">
              <strong>AI matte:</strong>{" "}
              {matteQuality === "fast" ? "U²-Net-p · fast (~1s)" : "BiRefNet · quality (~12s)"}
              {/* Genuinely low-RAM devices (≤4 GB reported; deviceMemory caps at 8) default to Fast
                  so the ~7 GB BiRefNet pass can't page and blow the timeout; note WHY here so the
                  operator understands the default (they can still flip to Quality). */}
              {matteQuality === "fast" && isLowMemoryDevice() ? " · low-RAM default" : ""}
              {" · "}
              {segInfo.executionProvider === "CPU" || segInfo.executionProvider === "none"
                ? `CPU${segInfo.gpuSupported && !segInfo.gpuRequested ? " — GPU off (measured slower for this model)" : segInfo.gpuRequested ? " — GPU requested but ops ran on CPU" : ""}`
                : `GPU (${segInfo.executionProvider})`}
              {" · "}
              {segInfo.modelLoaded ? "model loaded" : "model not loaded"}
              {segInfo.lastError ? (
                <div className="mask-editor__seg-error"><Icon name="warning" size={16} /> {segInfo.lastError}</div>
              ) : null}
            </div>
          )}
          {/* Compositor GPU/CPU diagnostic (spec 024) — fetched ASYNC once per aiBusy flip
              (mirrors the segInfo pattern above). The old inline call treated the returned
              Promise as a value and read non-existent fields, so it (a) ALWAYS rendered
              "Compositor: CPU" regardless of the real path, and (b) fired a native invoke —
              which triggers wgpu adapter init — on EVERY React render. */}
          {compInfo && (
            <div data-testid="compositor-info" className="faint mask-editor__seg-info">
              <strong>Compositor:</strong>{" "}
              {compInfo.lastPath === "gpu"
                ? `GPU${compInfo.adapterName ? ` (${compInfo.adapterName})` : ""}${compInfo.backend ? ` · ${compInfo.backend}` : ""} + multi-core CPU`
                : `CPU${compInfo.lastFallbackReason ? ` (${compInfo.lastFallbackReason})` : compInfo.gpuAvailable ? " (GPU idle — used on next render)" : ""}`}
            </div>
          )}
        </div>
    </>
  );
}
